/* global BigInt */
/**
 * Discount / purchase approval state and "may I decide it?" — the portal
 * mirror of backend/services/approvalLimitService.js (approvals spec
 * 2026-09-27). Only decides what to SHOW: the server re-checks the key, the
 * requester and the approver's own limit on every call.
 */
import { hasPermission } from './permissions';

/** Window event after a decision on the Approvals page; the sidebar badge refetches on it. */
export const APPROVALS_CHANGED = 'approvals-changed';

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Only the platform Super Admin is exempt: a limit set on the built-in Admin
// applies (owner ruling 2026-09-28; mirrors approvalLimitService.isUnlimitedRole).
// Exported for UserModal's self-edit lock: the only caller who may edit their
// own limits is the one the server exempts from the guard entirely.
export const isUnlimitedRole = (role) => !!role && role.name === 'Super Admin' && !role.company_id;

// Explicit null ('' from a form) = unlimited; a missing column (undefined) fails
// closed as 0 so a stale session user cannot see approve buttons.
const limitOf = (v) => (v === null || v === '' ? null : v === undefined ? 0 : Number(v));

// A discount limit is compared on the UNROUNDED amount, in integers, exactly
// as the server does (approvalLimitService.coversDiscount):
// amount_halalas × 10000 <= limit_basis_points × subtotal_halalas. A rounded
// percentage let SAR 400 off SAR 10,000,000 (0.004%) read as covered by 0%.
const hundredths = (v) => BigInt(Math.round((Number(v) || 0) * 100));
const negativeLineSum = (lines) =>
  round2(
    (lines || []).reduce((sum, line) => {
      const total =
        line.total_price != null && line.total_price !== ''
          ? Number(line.total_price)
          : (line.quantity === undefined ? 1 : Number(line.quantity)) * Number(line.unit_price);
      return Number.isFinite(total) && (total < 0 || Object.is(total, -0))
        ? sum + Math.max(-total, 0.01)
        : sum;
    }, 0)
  );
function coversDiscount(limit, amount, subtotal) {
  const a = hundredths(amount);
  if (a <= 0n) return true;
  const s = hundredths(subtotal);
  return s > 0n && a * 10000n <= hundredths(limit) * s;
}

/** { status, requestedBy, amount, percent?, reason } or null when there is no request. */
export function approvalState(kind, doc) {
  if (!doc) return null;
  if (kind === 'purchase') {
    if (!doc.approval_status) return null;
    return {
      status: doc.approval_status,
      requestedBy: doc.approval_requested_by || null,
      amount: Number(doc.total_amount) || 0,
      reason: doc.rejection_reason || null,
    };
  }
  if (!doc.discount_approval_status) return null;
  const amount = Number(doc.discount_requested_amount) || 0;
  const subtotal = Number(doc.subtotal) || 0;
  const negative = negativeLineSum(doc.items);
  return {
    status: doc.discount_approval_status,
    requestedBy: doc.discount_requested_by || null,
    amount,
    percent:
      subtotal + negative > 0 ? round2(((amount + negative) / (subtotal + negative)) * 100) : null,
    reason: doc.discount_rejection_reason || null,
  };
}

export function canDecide(user, kind, doc) {
  const state = approvalState(kind, doc);
  if (!user || !state || state.status !== 'pending') return false;
  if (state.requestedBy && state.requestedBy === user.id) return false;
  if (isUnlimitedRole(user.role)) return true;
  const resource = kind === 'purchase' ? 'purchase_invoices' : 'discounts';
  if (!hasPermission(user, resource, 'approve')) return false;
  const limit = limitOf(
    kind === 'purchase' ? user.purchase_approval_limit_sar : user.discount_approval_limit_pct
  );
  if (limit === null) return true;
  if (kind !== 'purchase') {
    const negative = negativeLineSum(doc.items);
    return coversDiscount(limit, state.amount + negative, (Number(doc.subtotal) || 0) + negative);
  }
  return state.amount <= limit + 0.005;
}

/**
 * True when `amount` off `subtotal` is above the writer's own discount limit —
 * the save will go to approval instead of applying. For the create pages'
 * hint; the server makes the real call.
 */
export function exceedsOwnDiscountLimit(user, amount, subtotal) {
  const a = Number(amount) || 0;
  if (!user || a <= 0 || isUnlimitedRole(user.role)) return false;
  const limit = limitOf(user.discount_approval_limit_pct);
  if (limit === null) return false;
  return !coversDiscount(limit, a, subtotal);
}

/**
 * The toast for a create/generate answer's discount_approval.status: the
 * discount went to approval (pending) — and, when `customerSend` is 'held',
 * nothing went to the customer yet (owner 2026-09-29: the decision sends it
 * once) — or the invoice was issued at once at full price and the request
 * expired with it. null when there is nothing to say.
 */
export function discountAnswerToastKey(status, customerSend) {
  if (status === 'pending' && customerSend === 'held') return 'appr.discountPendingSendHeld';
  if (status === 'pending') return 'appr.discountPendingSaved';
  if (status === 'expired') return 'appr.discountExpiredOnPost';
  return null;
}
